import { AppColors } from './color';
import { AppConfig } from './config';
import { configStore } from './configStore';

export function hexToRgb(hex: string): [number, number, number] {
  let c = hex.replace('#', '');
  if (c.length === 3) {
    c = c
      .split('')
      .map((x) => x + x)
      .join('');
  }
  if (c.length !== 6) {
    throw new Error('HEX 색상은 6자리여야 합니다');
  }
  const num = parseInt(c, 16);
  return [(num >> 16) & 255, (num >> 8) & 255, num & 255];
}

export function rgbToHex(r: number, g: number, b: number): string {
  const toHex = (c: number) => {
    const hex = c.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };
  return '#' + toHex(r) + toHex(g) + toHex(b);
}

export function rgbArrayToHex(rgb: [number, number, number]): string {
  return rgbToHex(rgb[0], rgb[1], rgb[2]);
}

export function hexToNormalizedRGB(hex: string): [number, number, number] {
  const rgb = hexToRgb(hex);
  return [rgb[0] / 255, rgb[1] / 255, rgb[2] / 255];
}

export function rgbToNormalizedRGB(
  rgb: [number, number, number],
): [number, number, number] {
  return [rgb[0] / 255, rgb[1] / 255, rgb[2] / 255];
}

// RGB 문자열을 정규화된 RGBA로 변환 (0~1 범위)
export function rgbToNormalizedRGBA(
  rgb: string,
): [number, number, number, number] {
  const match = rgb.match(/\d+/g);
  if (!match || match.length < 3) return [1, 0, 0, 1];
  return [
    parseFloat(match[0]) / 255,
    parseFloat(match[1]) / 255,
    parseFloat(match[2]) / 255,
    1,
  ];
}

export function rgbToNormalizedARGB(
  argb: [number, number, number, number],
): [number, number, number, number] {
  return [argb[0], argb[1] / 255, argb[2] / 255, argb[3] / 255];
}

// RGB 문자열을 정수형 RGBA로 변환 (0~255 범위)
export function rgbToUintRGBA(rgb: string): [number, number, number, number] {
  const match = rgb.match(/\d+/g);
  if (!match || match.length < 3) return [255, 0, 0, 255];
  return [parseInt(match[0]), parseInt(match[1]), parseInt(match[2]), 255];
}

export const updateAVCColor = ({
  aortacolor,
  avccolor,
}: {
  aortacolor?: [number, number, number];
  avccolor?: [number, number, number];
}) => {
  let ac = configStore.get().aorta_colors.slice();

  if (aortacolor) {
    const diff = configStore
      .get()
      .aorta_indexs.filter(
        (x) => !new Set(configStore.get().avc_indexs).has(x),
      );
    for (const i of diff) {
      ac[i][1] = aortacolor[0];
      ac[i][2] = aortacolor[1];
      ac[i][3] = aortacolor[2];
    }
  }

  if (avccolor) {
    for (const i of configStore.get().avc_indexs) {
      ac[i][1] = avccolor[0];
      ac[i][2] = avccolor[1];
      ac[i][3] = avccolor[2];
    }
  }

  configStore.setData({ aorta_colors: ac });
  return ac;
};

export const updateAVCOpacity = ({
  aortaopacity,
  avcopacity,
  aortacolor,
}: {
  aortaopacity: number;
  avcopacity: number;
  aortacolor: [number, number, number];
}) => {
  let ac = configStore.get().aorta_colors.slice();
  let ao = configStore.get().aorta_opacitys.slice();

  for (const i of configStore.get().aorta_indexs) {
    ao[i][1] = aortaopacity;
  }

  if (aortaopacity > 0) {
    const setB = new Set(configStore.get().avc_indexs);
    const inter = configStore.get().aorta_indexs.filter((x) => setB.has(x));
    for (const i of inter) {
      ao[i][1] = aortaopacity;
    }
    if (avcopacity === 0) {
      for (const i of inter) {
        ac[i][1] = aortacolor[0];
        ac[i][2] = aortacolor[1];
        ac[i][3] = aortacolor[2];
      }
    }
  } else {
    for (const i of configStore.get().avc_indexs) {
      ao[i][1] = avcopacity;
    }
  }

  configStore.setData({ aorta_colors: ac, aorta_opacitys: ao });
  return ao;
};

export function makeCoronaryColorPoints(
  colors: AppColors,
): Array<[number, number, number, number]> {
  let colorPoints: Array<[number, number, number, number]> = Array.from(
    { length: 256 },
    (_, i) => [i, 0, 0, 0],
  );
  colorPoints[1] = [1, ...hexToNormalizedRGB(colors.lowAttenuation)];
  colorPoints[2] = [2, ...hexToNormalizedRGB(colors.nonCalcified)];
  colorPoints[3] = [3, ...hexToNormalizedRGB(colors.CALCIUM)];
  colorPoints[251] = [251, ...hexToNormalizedRGB(colors.FAI4)];
  colorPoints[252] = [252, ...hexToNormalizedRGB(colors.FAI3)];
  colorPoints[253] = [253, ...hexToNormalizedRGB(colors.FAI2)];
  colorPoints[254] = [254, ...hexToNormalizedRGB(colors.FAI1)];
  colorPoints[255] = [255, ...hexToNormalizedRGB(colors.LUMEN)];
  return colorPoints;
}

export function makeOpacityPoints(
  type: string,
  opaval?: [string, number, string],
) {
  let locOpacityPoints: Array<[number, number]> = Array.from(
    { length: 256 },
    (_, i) => [i, 0],
  );
  let setOpacity = defaultCoronaryOpacity.slice();
  if (type === 'fai') {
    setOpacity = faiopacitydata;
  } else if (
    type === 'plaque'
    // || type === 'stenosis'
    // || type === 'isr'
  ) {
    setOpacity = plaqueopacitydata;
  }  else if (type === 'stenosis' || type === 'isr') {
    // Stenosis/ISR should start with only the lumen label visible.
    // Plaque/FAI segment opacities are disabled, but lumen (255) must stay on.
    setOpacity = setOpacity.map(([idx]) => [idx, idx === 255 ? 0.5 : 0]);
  }
  if (opaval !== undefined && opaval[2] === 'segmanager') {
    if (opaval !== undefined) {
      if (opaval[0] === 'coronary') {
        for (let i = 0; i < setOpacity.length; i++) {
          if (setOpacity[i][0] === 255) {
            setOpacity[i][1] = opaval[1];
          }
        }
      } else if (opaval[0] === 'calcium' && type !== 'fai') {
        for (let i = 0; i < setOpacity.length; i++) {
          if (setOpacity[i][0] === 3) {
            setOpacity[i][1] = opaval[1];
          }
        }
      }
    }
  }
  for (const opa of setOpacity) {
    const [idx, val] = opa;
    locOpacityPoints[idx] = opa;
  }
  return locOpacityPoints;
}

export const plaqueopacitydata: Array<[number, number]> = [
  [1, 0.5],
  [2, 0.5],
  [3, 0.5],
  [251, 0],
  [252, 0],
  [253, 0],
  [254, 0],
  [255, 0.5],
];

export const faiopacitydata: Array<[number, number]> = [
  [1, 0],
  [2, 0],
  [3, 0],
  [251, 0.5],
  [252, 0.5],
  [253, 0.5],
  [254, 0.5],
  [255, 0.5],
];

export const defaultCoronaryOpacity: Array<[number, number]> = [
  [1, 0.5],
  [2, 0.5],
  [3, 0.5],
  [251, 0.5],
  [252, 0.5],
  [253, 0.5],
  [254, 0.5],
  [255, 0.5],
];

// colorUtils.ts
type keyname = {
  [K in keyof AppColors]: AppColors[K] extends string[] ? K : never
}[keyof AppColors];

export function getPalette(
  name: keyname,
  colors: AppColors,
): Array<[number, number, number, number]> {
  return colors[name].map((hex) => [...hexToNormalizedRGB(hex), 1]);
}

export type volumeColorType = 'none' | 'warm' | 'red' | 'orangeRed' | 'MIP';

export function makeVolumeColorPoints(
  type: volumeColorType = 'warm',
  hu: { min: number; max: number },
): [number, string][] {
  let rawPoints: [number, string][] = [];
  const BASE_POINTS: [number, string][] = [
    [-2048, '#000000ff'],
    [4096, '#ffffffff'],
  ];
  if (type === 'warm') {
    rawPoints = [
      [-1000, '#1a0000ff'],
      [-500, '#5b0000ff'],
      [0, '#8d2b04ff'],
      [20, '#e84a00ff'],
      [60, '#f76b00ff'],
      [150, '#ce6441ff'],
      [200, '#d4c2c2ff'],
      [250, '#f0e0d0ff'],
      [700, '#faf7f3ff'],
      [1000, '#fffbfbff'],
    ];
  } else if (type === 'red') {
    rawPoints = [
      [-1000, '#1a0000ff'],
      [-500, '#5b0000ff'],
      [0, '#8c0000ff'],
      [20, '#b80000ff'],
      [60, '#e00000ff'],
      [150, '#ff4040ff'],
      [200, '#d4c2c2ff'],
      [250, '#e0e0e0'],
      [700, '#f7f7f7'],
      [1000, '#fffbfbff'],
    ];
  } else if (type === 'orangeRed') {
    rawPoints = [
      [-1000, '#1a0000ff'],
      [-500, '#5b0000ff'],
      [0, '#8d2b04ff'],
      [20, '#d43000ff'],
      [60, '#f06000ff'],
      [150, '#ffb000ff'],
      [200, '#d4c2c2ff'],
      [250, '#e0e0e0'],
      [700, '#f7f7f7'],
      [1000, '#fffbfbff'],
    ];
  } else if (type === 'none') {
    rawPoints = [
      [-1024, '#00000000'],
      [128, '#00000000'],
      [129, '#9d000419'],
      [173, '#e774007a'],
      [255, '#e2e2e2db'],
      [584, '#f7f7f7dd'],
    ];
  } else if (type === 'MIP') {
    rawPoints = [
      [-1024, '#00000000'],
      [200, '#e2e2e2db'],
      [584, '#f7f7f7dd'],
    ];
  }
  return [
    BASE_POINTS[0],
    [hu.min, '#000000ff'],
    ...rawPoints,
    [hu.max, '#ffffffff'],
    BASE_POINTS[1],
  ];
}
